import { useContext, useEffect, useState } from "react"
import { AppContext } from "../App"
import { ROLE_LOAN_OFFICER, canAccessNavItem } from "../accessControl";
import { callApi, getInlineLoader } from "../Helpers"
import { MdHandshake, MdCalendarToday } from "react-icons/md";

export default function View({ uuid }) {

    const appContext = useContext(AppContext);

    const [ready, setReady] = useState(false);
    const [loading, setLoading] = useState(false);

    const [customerUuid, setCustomerUuid] = useState('');
    const [motorcycleUuid, setMotorcycleUuid] = useState('');
    const [saleType, setSaleType] = useState('');
    const [repaymentType, setRepaymentType] = useState('weekly');
    const [assignedTo, setAssignedTo] = useState('');
    const [dueDate, setDueDate] = useState('');
    
    // New State for Backdating Sales/Contracts (Defaults to Today)
    const [contractDate, setContractDate] = useState(new Date().toISOString().split('T')[0]);
    
    // Modern physical tracking & operational properties
    const [regNumber, setRegNumber] = useState('');
    const [gpsTrackerNumber, setGpsTrackerNumber] = useState('');
    const [durationMonths, setDurationMonths] = useState('16'); // Default set to 16 months

    const [data, setData] = useState(null);

    // Compute the real-time available stock of the currently selected motorcycle model
    const getSelectedModelStock = () => {
        if (!motorcycleUuid || !appContext.allMotorcycles) return null;
        const matchedBike = appContext.allMotorcycles.find(item => item.uuid === motorcycleUuid);
        if (!matchedBike) return 0;
        
        const total = parseInt(matchedBike.totalReceived) || 0;
        const allocated = parseInt(matchedBike.allocatedCount) || 0;
        const available = total - allocated;
        return available > 0 ? available : 0;
    };

    async function init() {
        setReady(false);
        if (!canAccessNavItem(appContext.currentUserData, "sales")) {
            appContext.tellError("You do not have access to sales.");
            appContext.navTo({ item: "dashboard" });
            return;
        }

        await Promise.all([
            appContext.getAllSales(),
            appContext.getAllCustomers(),
            appContext.getAllMotorcycles(),
            appContext.getAllAdmins(),
            (async () => {
                if (uuid) await getData();
            })(),
        ]);
        setReady(true);
    }

    async function getData() {
        await callApi('get-sale-data', { uuid }).then((response) => {
            if (response.status === 1) {
                setData(response.data);
                setCustomerUuid(response.data.customerID || '');
                setMotorcycleUuid(response.data.motorcycleID || '');
                setSaleType(response.data.saleType || '');
                setRepaymentType(response.data.repaymentType || '');
                setDueDate(response.data.dueDate || '');
                setRegNumber(response.data.regNumber || '');
                setGpsTrackerNumber(response.data.gpsTrackerNumber || '');
                setDurationMonths(response.data.durationMonths ? String(response.data.durationMonths) : '16');
                
                // Load existing date if it's an edit, otherwise keep today's date
                if (response.data.contractDate) {
                    setContractDate(response.data.contractDate);
                }
            } else {
                appContext.tellError('Could not fetch data');
            }
        });
    }

    async function create() {
        if (!customerUuid) return appContext.tellError("Customer field is compulsory");
        if (!motorcycleUuid) return appContext.tellError("Motorcycle selection is compulsory");
        if (!saleType) return appContext.tellError("Sale Type classification is compulsory");
        if (!contractDate) return appContext.tellError("Contract Date is compulsory");
        if (!assignedTo) return appContext.tellError("Loan Officer not Selected");

        if (saleType !== 'cash') {
            if (!regNumber.trim()) return appContext.tellError("Registration Number is compulsory");
            if (!gpsTrackerNumber.trim()) return appContext.tellError("GPS Tracker ID is compulsory");
        }

        if (saleType === 'loan') {
            if (!repaymentType) return appContext.tellError("Repayment Contract is compulsory");
            if (!durationMonths || parseInt(durationMonths) <= 0) return appContext.tellError("Contract Duration is compulsory");
        }

        setLoading(true);
        await callApi('create-sale', {
            customerUuid,
            motorcycleUuid,
            saleType,
            repaymentType,
            assignedTo,
            contractDate, // Passed to backend
            regNumber: saleType === 'cash' ? null : regNumber.trim().toUpperCase(),
            gpsTrackerNumber: saleType === 'cash' ? null : gpsTrackerNumber.trim(),
            durationMonths: saleType === 'loan' ? parseInt(durationMonths) : null
        }).then(response => {
            if (response.status === 1) {
                appContext.tellMessage('Successful');
                appContext.navBack();
                appContext.getAllSales();
            } else {
                appContext.tellError(response.msg);
            }
        });
        setLoading(false);
    }

    async function update() {
        if (!customerUuid) return appContext.tellError("Customer field is compulsory");
        if (!motorcycleUuid) return appContext.tellError("Motorcycle selection is compulsory");
        if (!saleType) return appContext.tellError("Sale Type classification is compulsory");
        if (!contractDate) return appContext.tellError("Contract Date is compulsory");
        
        if (saleType !== 'cash') {
            if (!regNumber.trim()) return appContext.tellError("Registration Number is compulsory");
            if (!gpsTrackerNumber.trim()) return appContext.tellError("GPS Tracker ID is compulsory");
        }

        if (saleType === 'loan') {
            if (!repaymentType) return appContext.tellError("Repayment Contract is compulsory");
            if (!durationMonths || parseInt(durationMonths) <= 0) return appContext.tellError("Contract Duration is compulsory");
        }

        setLoading(true);
        await callApi('update-sale', {
            uuid,
            customerUuid,
            motorcycleUuid,
            saleType,
            repaymentType,
            dueDate,
            assignedTo,
            contractDate, // Passed to backend
            regNumber: saleType === 'cash' ? null : regNumber.trim().toUpperCase(),
            gpsTrackerNumber: saleType === 'cash' ? null : gpsTrackerNumber.trim(),
            durationMonths: saleType === 'loan' ? parseInt(durationMonths) : null
        }).then(response => {
            if (response.status === 1) {
                appContext.tellMessage('Successful');
                appContext.navBack();
                appContext.getAllSales();
            } else {
                appContext.tellError(response.msg);
            }
        });
        setLoading(false);
    }

    useEffect(() => {
        init();
    }, [uuid]);

    useEffect(() => {
        if (saleType === 'cash') {
            setRepaymentType('');
            setDueDate('');
            setDurationMonths('');
            setRegNumber(''); // Clear GPS and Reg to prevent stale data
            setGpsTrackerNumber('');
        } else if (saleType === 'loan') {
            setRepaymentType('weekly'); 
            setDurationMonths('16');
        }
    }, [saleType]);

    useEffect(() => {
        if (data) {
            setCustomerUuid(data.customerID || '');
            setMotorcycleUuid(data.motorcycleID || '');
            setSaleType(data.saleType || '');
            setRepaymentType(data.repaymentType || '');
            setDueDate(data.dueDate || '');
            setRegNumber(data.regNumber || '');
            setGpsTrackerNumber(data.gpsTrackerNumber || '');
            setDurationMonths(data.durationMonths ? String(data.durationMonths) : '');
            setAssignedTo(data.assignedTo || '');
            if (data.contractDate) setContractDate(data.contractDate);
        }
    }, [data]);

    useEffect(() => {
        appContext.setShowOverlayLoader(loading);
        return () => appContext.setShowOverlayLoader(false);
    }, [loading]);

    if (!ready) {
        return (
            <div className="container mSupportLoading">
                {getInlineLoader()}
            </div>
        );
    }

    return (
        <div className="container">
            <div className="app-form-page">
                <div className="app-form-shell">
                    <div className="app-form-header">
                        <div>
                            <h6 className="app-form-title">{uuid ? "Edit Sale Record" : "Add Sale Transaction"}</h6>
                        </div>
                        <div className="app-form-badge">
                            <MdHandshake size={18} />
                            {uuid ? "Updating contract" : "Creating contract"}
                        </div>
                    </div>

                    <div className="app-form-section">
                        <div className="app-form-summary-grid">
                            <div className="app-form-summary-card">
                                <span>Customers</span>
                                <strong>{appContext.allCustomers?.length || 0} available</strong>
                            </div>
                            <div className="app-form-summary-card">
                                <span>Motorcycles</span>
                                <strong>{appContext.allMotorcycles?.length || 0} models available</strong>
                            </div>
                            <div className="app-form-summary-card">
                                <span>Sale Mode</span>
                                <strong>{saleType ? saleType.toUpperCase() : "Unselected"}</strong>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6 mt-3 mb-3">
                                <div className="app-form-input-wrap">
                                    <label className="form-label">Customer *</label>
                                    <select value={customerUuid} onChange={(e) => setCustomerUuid(e.target.value)} className="form-control" required>
                                        <option value="">--Select Customer--</option>
                                        {appContext.allCustomers?.map((item, i) => (
                                            <option key={i} value={item.uuid}>{item.fullname}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="col-md-6 mt-3 mb-3">
                                <div className="app-form-input-wrap">
                                    <label className="form-label">Motorcycle Model *</label>
                                    <select value={motorcycleUuid} onChange={(e) => setMotorcycleUuid(e.target.value)} className="form-control" required>
                                        <option value="">--Select Motorcycle--</option>
                                        {appContext.allMotorcycles?.filter(item => {
                                            const total = parseInt(item.totalReceived) || 0;
                                            const allocated = parseInt(item.allocatedCount) || 0;
                                            const availableStock = total - allocated;
                                            
                                            if (uuid && item.uuid === motorcycleUuid) return true;
                                            
                                            return availableStock > 0;
                                        }).map((item, i) => (
                                            <option key={i} value={item.uuid}>{item.model}</option>
                                        ))}
                                    </select>
                                    
                                    {/* Real-time Inline Specific Model Stock Quantifier */}
                                    {motorcycleUuid && (
                                        <div className="text-primary small fw-bold mt-1 px-1">
                                            {getSelectedModelStock()} units available for this model
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="col-md-6 mt-3 mb-3">
                                <div className="app-form-input-wrap">
                                    <label className="form-label">Sale Configuration Type *</label>
                                    <select value={saleType} onChange={(e) => setSaleType(e.target.value)} className="form-control" required>
                                        <option value="">--Select Sale Type--</option>
                                        <option value="cash">Cash Payment</option>
                                        <option value="loan">Financing Hire-Purchase (Loan)</option>
                                    </select>
                                </div>
                            </div>

                            {/* NEW: Contract Date Input */}
                            <div className="col-md-6 mt-3 mb-3">
                                <div className="app-form-input-wrap">
                                    <label className="form-label d-flex align-items-center gap-2">
                                        <MdCalendarToday size={16} /> Contract / Sale Date *
                                    </label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={contractDate}
                                        max={new Date().toISOString().split('T')[0]} // Prevents selecting future dates
                                        onChange={(e) => setContractDate(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            {saleType !== 'cash' && (
                                <>
                                    <div className="col-md-6 mt-3 mb-3">
                                        <div className="app-form-input-wrap">
                                            <label className="form-label">Registration Number *</label>
                                            <input 
                                                className="form-control"
                                                value={regNumber}
                                                onChange={(e) => setRegNumber(e.target.value)}
                                                placeholder="e.g. MC 542 DFR"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6 mt-3 mb-3">
                                        <div className="app-form-input-wrap">
                                            <label className="form-label">GPS Tracker Device ID *</label>
                                            <input 
                                                className="form-control"
                                                value={gpsTrackerNumber}
                                                onChange={(e) => setGpsTrackerNumber(e.target.value)}
                                                placeholder="Enter tracking system serial ID"
                                                required
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            {saleType === 'loan' && (
                                <>
                                    <div className="col-md-3 mt-3 mb-3">
                                        <div className="app-form-input-wrap">
                                            <label className="form-label">Duration (Months) *</label>
                                            <input 
                                                className="form-control"
                                                type="number"
                                                min="1"
                                                value={durationMonths}
                                                onChange={(e) => setDurationMonths(e.target.value)}
                                                placeholder="e.g. 16"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-3 mt-3 mb-3">
                                        <div className="app-form-input-wrap">
                                            <label className="form-label">Assign Loan Officer</label>
                                            
                                            <select 
                                                value={assignedTo} 
                                                onChange={(e) => setAssignedTo(e.target.value)} 
                                                className="form-control" 
                                                required
                                            >
                                                <option value="">--Select Officer--</option>
                                                {appContext.allAdmins
                                                    ?.filter(admin => admin.role === ROLE_LOAN_OFFICER || admin.role === 'loan_officer')
                                                    ?.map((item, i) => (
                                                        <option key={i} value={item.id}>
                                                            {item.fullname}
                                                        </option>
                                                    ))
                                                }
                                            </select>
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="col-12 mt-3 mb-3 text-end app-form-submit-row">
                                <button
                                    className="btn btn-primary btn-block app-form-submit-btn"
                                    onClick={uuid ? update : create}
                                >
                                    {uuid ? "UPDATE SALE CONTRACT" : "REGISTER LEASE TRANSACTION"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}